<template>
  <div class="page flex-col">
    <AppHeader />

    <!-- 顶部 Banner -->
    <div class="pd-banner">
      <div class="pd-banner-mask">
        <span class="pd-banner-zh">{{ sectionName }}</span>
      </div>
    </div>

    <div class="pd-main">
      <!-- 面包屑 -->
      <nav class="pd-breadcrumb">
        <router-link :to="homeLink">{{ isEnglish ? 'Home' : '首页' }}</router-link>
        <span class="pd-sep">&gt;</span>
        <router-link :to="listLink">{{ sectionName }}</router-link>
        <span class="pd-sep">&gt;</span>
        <router-link :to="listLink">{{ categoryLabel }}</router-link>
        <span class="pd-sep">&gt;</span>
        <span class="pd-crumb-active">{{ product.model || product.name }}</span>
      </nav>

      <!-- 页标题 -->
      <div class="pd-title-row">
        <span class="pd-title-zh">{{ sectionName }}</span>
        <span class="pd-title-en">PRODUCTS</span>
      </div>
      <div class="pd-title-line"></div>

      <!-- 侧栏 + 详情 -->
      <div class="pd-body">
        <aside class="pd-sidebar">
          <button
            v-for="(cat, index) in categories"
            :key="cat.id || 'cat_' + index"
            type="button"
            class="pd-side-item"
            :class="{ active: cat.id === activeCategoryId }"
            @click="onCategory(cat)"
          >
            {{ categoryDisplayName(cat) }}
          </button>
        </aside>

        <div class="pd-content">
          <h1 class="pd-product-name">{{ product.name }}</h1>

          <div class="pd-desc">
            <p v-if="product.features" class="pd-desc-text">
              <span class="pd-desc-label">{{ featuresLabel }}</span>{{ product.features }}
            </p>
            <p v-if="product.application" class="pd-desc-text">
              <span class="pd-desc-label">{{ applicationLabel }}</span>{{ product.application }}
            </p>
          </div>

          <div class="pd-detail-row" :class="{ 'pd-detail-row--stack': !hasRichSpecs }">
            <div class="pd-hero-img-wrap">
              <img
                class="pd-hero-img"
                :src="productImage"
                :alt="product.name"
              />
            </div>

            <div v-if="hasSpecs" class="pd-spec-wrap">
              <div class="pd-spec-title">{{ specsTitle }}</div>
              <table class="pd-spec-table">
                <tbody>
                  <tr v-for="(row, index) in product.specs" :key="'spec_' + index">
                    <th>{{ row.label }}</th>
                    <td>{{ row.value }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import { isEnglishRoute, langLink, siteHomeLink } from '@/utils/lang';

const defaultHeroImg = require('./assets/img/FigmaDDSSlicePNG2a0fb788776a372de3c8c890e2a77ed7.png');
const defaultGunImg = require('./assets/img/FigmaDDSSlicePNGa29268579a1f66a27760df06030fc70a.png');

const CATEGORY_EN = {
  arc: 'Arc Spray ARC',
  hvaf: 'High Velocity Air Fuel HVAF',
  hvof: 'High Velocity Oxygen Fuel HVOF',
  aps: 'Plasma Spray APS',
  'arc-gun': 'Arc Spray Gun',
  'hvaf-gun': 'HVAF Spray Gun',
  'hvof-gun': 'HVOF Spray Gun',
  'aps-gun': 'APS Spray Gun',
  powder: 'Spray Powders / Wires',
  parts: 'Wear Parts',
  prep: 'Surface Preparation',
  control: 'Control & Powder Feed',
  robot: 'Automation'
};

const fallbackProduct = {
  model: 'HR-ARC-400A',
  name: 'KZ-400喷锌机/镀铝机',
  features: '平特性电源；低电压稳定喷涂；降低碳烧损；电弧稳定；自调弧能力好。',
  application: '适合典型长效防腐喷锌或喷铝，广泛应用水闸、桥梁、风电、储罐、船体和钢结构等。',
  category: 'arc',
  specs: [
    { label: '型号', value: 'HR-ARC-400A' },
    { label: '输入电源', value: '3相AC 380V 50Hz' },
    { label: '输入功率', value: '18.8 KVA' },
    { label: '输入电流', value: 'AC 22A' },
    { label: '输入空载电压', value: 'DC 20-45V' },
    { label: '电压调节级数', value: '粗细一体共9档' },
    { label: '电源特性', value: '平特性' },
    { label: '输出电流', value: 'DC 400A' },
    { label: '负载率', value: '80% @ 400A' },
    { label: '绝缘等级', value: 'B级' },
    { label: '外形尺寸', value: '700*500*1010 mm' },
    { label: '重量', value: '220 kg' },
    { label: '压缩空气压力', value: '≥0.6 MPa' }
  ]
};

export default {
  name: 'ProductDetailARC',
  data() {
    return {
      sections: [],
      detailsMap: {},
      fallbackProduct
    };
  },
  computed: {
    isEnglish() {
      return isEnglishRoute(this.$route);
    },
    homeLink() {
      return siteHomeLink(this.$route);
    },
    productId() {
      return this.$route.query.id || 'HR-ARC-400A';
    },
    activeSectionId() {
      return this.$route.query.section || this.product.section || 'equipment';
    },
    activeCategoryId() {
      return this.$route.query.cat || this.product.category || '';
    },
    activeSection() {
      return this.sections.find((s) => s.id === this.activeSectionId) || null;
    },
    sectionName() {
      if (this.isEnglish) {
        return (this.activeSection && this.activeSection.nameEn) || 'Thermal spraying products';
      }
      return (this.activeSection && this.activeSection.name) || '热喷涂设备';
    },
    categories() {
      return (this.activeSection && this.activeSection.categories) || [];
    },
    activeCategory() {
      return this.categories.find((c) => c.id === this.activeCategoryId) || this.categories[0] || null;
    },
    categoryLabel() {
      if (!this.activeCategory) return '';
      return this.categoryDisplayName(this.activeCategory);
    },
    listLink() {
      return langLink(
        '/products/catalog',
        {
          section: this.activeSectionId,
          cat: this.activeCategoryId || undefined
        },
        this.$route
      );
    },
    /** 优先从列表产品取详情（Highlights / Industries / Parameter） */
    product() {
      const listItem = this.findProductItem(this.productId);
      if (listItem) {
        return this.mapListProduct(listItem);
      }
      const fromMap = this.findDetailById(this.productId);
      if (fromMap) {
        return this.enrichProduct(fromMap);
      }
      if (this.detailsMap.default) {
        return this.enrichProduct(this.detailsMap.default);
      }
      return this.fallbackProduct;
    },
    productImage() {
      if (this.product.image) return this.product.image;
      const section = this.product.section || this.$route.query.section || '';
      if (section === 'guns') return defaultGunImg;
      return defaultHeroImg;
    },
    /**
     * detailType 定义在 categories 上（同一大类下可混用设备/材料）
     * 优先级：产品 > 分类 > 默认 equipment
     * equipment：设备类（图+参数表）
     * material：材料类（特性/应用+图，不展示参数表）
     */
    detailType() {
      return this.product.detailType || this.resolveDetailType(this.product) || 'equipment';
    },
    isEquipmentDetail() {
      return this.detailType === 'equipment';
    },
    isMaterialDetail() {
      return this.detailType === 'material';
    },
    hasSpecs() {
      if (!this.isEquipmentDetail) return false;
      return Array.isArray(this.product.specs) && this.product.specs.length > 0;
    },
    hasRichSpecs() {
      return this.hasSpecs && this.product.specs.length > 2;
    },
    specsTitle() {
      if (this.isEnglish) return 'Specifications';
      return this.isEquipmentDetail ? '设备参数' : '规格参数';
    },
    featuresLabel() {
      if (this.isEnglish) {
        return this.isMaterialDetail ? 'Description:' : 'Key Features:';
      }
      return this.isMaterialDetail ? '产品说明：' : '核心特性：';
    },
    applicationLabel() {
      if (this.isEnglish) {
        return this.isMaterialDetail ? 'Applications:' : 'Applications:';
      }
      return this.isMaterialDetail ? '适用场景：' : '应用范围：';
    }
  },
  created() {
    this.fetchDeviceData();
  },
  methods: {
    categoryDisplayName(cat) {
      if (!cat) return '';
      if (!this.isEnglish) return cat.label || cat.name || '';
      return cat.nameEn || cat.labelEn || CATEGORY_EN[cat.id] || cat.label || cat.name || '';
    },
    async fetchDeviceData() {
      try {
        const res = await fetch('/data/device.json');
        if (!res.ok) return;
        const data = await res.json();
        this.sections = data.sections || [];
        this.detailsMap = data.details || {};
      } catch (err) {
        console.warn('Failed to fetch device.json:', err);
      }
    },
    idEquals(a, b) {
      return String(a || '').toLowerCase() === String(b || '').toLowerCase();
    },
    findProductItem(id) {
      for (const section of this.sections) {
        for (const cat of section.categories || []) {
          const found = (cat.products || []).find((p) => this.idEquals(p.id, id));
          if (found) {
            return {
              ...found,
              section: section.id,
              category: cat.id,
              detailType: this.resolveDetailTypeFrom(found, cat)
            };
          }
        }
      }
      return null;
    },
    /** 仅从分类（及可选产品覆盖）读取 detailType */
    resolveDetailTypeFrom(product, category) {
      return (
        (product && product.detailType) ||
        (category && category.detailType) ||
        'equipment'
      );
    },
    resolveDetailType(mapped) {
      if (!mapped) return 'equipment';
      if (mapped.detailType) return mapped.detailType;
      const section = this.sections.find((s) => s.id === (mapped.section || this.activeSectionId));
      const cat = ((section && section.categories) || []).find(
        (c) => c.id === (mapped.category || this.activeCategoryId)
      );
      return this.resolveDetailTypeFrom(mapped, cat);
    },
    findDetailById(id) {
      if (!this.detailsMap) return null;
      if (this.detailsMap[id]) return this.detailsMap[id];
      const key = Object.keys(this.detailsMap).find((k) => this.idEquals(k, id));
      return key ? this.detailsMap[key] : null;
    },
    /**
     * 中英字段映射：
     * Highlights / HighlightsEn → features
     * Industries / IndustriesEn → application
     * Parameter / ParameterEn → specs（按 isEnglish 选择）
     */
    mapListProduct(item) {
      const en = this.isEnglish;
      const features = en
        ? item.HighlightsEn || item.Highlights || item.features || ''
        : item.Highlights || item.features || '';
      const application = en
        ? item.IndustriesEn || item.Industries || item.application || ''
        : item.Industries || item.application || '';
      let specs = en ? item.ParameterEn : item.Parameter;
      if (!Array.isArray(specs) || !specs.length) {
        specs = en ? item.Parameter : item.ParameterEn;
      }
      if (!Array.isArray(specs) || !specs.length) {
        specs = item.specs || [];
      }
      const modelFromParam = (specs || []).find((row) =>
        ['型号', 'Model', 'model'].includes(row && row.label)
      );
      return {
        id: item.id,
        name: en ? item.nameEn || item.name || item.id : item.name || item.id,
        model: (modelFromParam && modelFromParam.value) || item.id,
        features,
        application,
        specs: Array.isArray(specs) ? specs : [],
        image: item.image || '',
        section: item.section || this.$route.query.section || 'equipment',
        category: item.category || this.$route.query.cat || '',
        detailType: item.detailType || 'equipment'
      };
    },
    enrichProduct(detail) {
      const en = this.isEnglish;
      return {
        ...detail,
        specs: en
          ? detail.ParameterEn || detail.specs || detail.Parameter || []
          : detail.Parameter || detail.specs || [],
        features: en
          ? detail.HighlightsEn || detail.features || detail.Highlights || ''
          : detail.features || detail.Highlights || '',
        application: en
          ? detail.IndustriesEn || detail.application || detail.Industries || ''
          : detail.application || detail.Industries || '',
        section: detail.section || this.$route.query.section || 'equipment',
        category: detail.category || this.$route.query.cat || '',
        detailType: detail.detailType || this.resolveDetailType(detail)
      };
    },
    onCategory(cat) {
      if (!cat || !cat.id) return;
      this.$router.push(
        langLink(
          '/products/catalog',
          {
            section: this.activeSectionId,
            cat: cat.id
          },
          this.$route
        )
      );
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* 顶部 Banner */
.pd-banner {
  width: 100%;
  height: 360px;
  background: url(./assets/img/FigmaDDSSlicePNG4bfbddd95358d0de5c2a6890b148e575.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.pd-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
}

.pd-banner-mask {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pd-banner-zh {
  color: #fff;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.pd-main {
  width: 1550px;
  max-width: 92%;
  margin: 0 auto;
  padding: 28px 0 72px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pd-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
}

.pd-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.pd-breadcrumb a:hover {
  color: #1e40af;
}

.pd-sep {
  color: #9ca3af;
}

.pd-crumb-active {
  color: #1e40af;
}

.pd-title-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.pd-title-zh {
  font-size: 32px;
  font-weight: 900;
  color: #1e40af;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-title-en {
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 1px;
}

.pd-title-line {
  width: 100%;
  height: 2px;
  background: #93c5fd;
  margin: 16px 0 32px;
}

.pd-body {
  width: 100%;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 40px;
  align-items: start;
}

.pd-sidebar {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pd-side-item {
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  text-align: left;
  padding: 18px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: #1e40af;
  background: #dbeafe;
  transition: background 0.2s ease, color 0.2s ease;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-side-item:hover {
  background: #bfdbfe;
}

.pd-side-item.active {
  background: #1e40af;
  color: #fff;
}

.pd-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pd-product-name {
  margin: 0 0 20px;
  font-size: 26px;
  font-weight: 800;
  color: #1e40af;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-desc {
  width: 100%;
  margin-bottom: 28px;
}

.pd-desc p {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.9;
  color: #374151;
  white-space: pre-line;
}

.pd-desc p:last-child {
  margin-bottom: 0;
}

.pd-desc-text {
  white-space: pre-line;
}

.pd-desc-label {
  font-weight: 700;
  color: #111827;
}

.pd-detail-row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}

.pd-detail-row--stack {
  grid-template-columns: 1fr;
}

.pd-hero-img-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
  border-radius: 8px;
  padding: 24px 16px;
  box-sizing: border-box;
  min-height: 420px;
}

.pd-hero-img {
  max-width: 100%;
  max-height: 480px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.pd-spec-wrap {
  width: 100%;
  min-width: 0;
}

.pd-spec-title {
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 800;
  color: #1e40af;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-spec-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 14px;
  line-height: 1.4;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-spec-table th,
.pd-spec-table td {
  padding: 12px 16px;
  border: none;
  vertical-align: middle;
}

.pd-spec-table td {
  white-space: pre-line;
}

.pd-spec-table th {
  width: 38%;
  text-align: left;
  font-weight: 600;
  color: #374151;
}

.pd-spec-table td {
  color: #4b5563;
}

.pd-spec-table tr:nth-child(odd) th,
.pd-spec-table tr:nth-child(odd) td {
  background: #eef1f4;
}

.pd-spec-table tr:nth-child(even) th,
.pd-spec-table tr:nth-child(even) td {
  background: #fff;
}

@media (max-width: 1100px) {
  .pd-body {
    grid-template-columns: 1fr;
  }

  .pd-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .pd-side-item {
    width: auto;
    flex: 1 1 200px;
  }

  .pd-detail-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .pd-banner {
    height: 220px;
  }

  .pd-banner-zh {
    font-size: 30px;
  }

  .pd-title-zh {
    font-size: 26px;
  }

  .pd-product-name {
    font-size: 22px;
  }

  .pd-hero-img-wrap {
    min-height: 280px;
  }
}
</style>
